import { CreateTextBlockReq, DeleteTextBlockReq, UpdateReorderListReq, UpdateTextBlockReq } from '../../dto/ReqDtoRepository';
import { DeleteResponse, TextBlockListResponse, TextBlockResponse } from '../../dto/ResDtoRepository';
import { createTextBlock, deleteTextBlock, getTextBlockListForOwner, updateTextBlock, reorderTextBlock } from '../../api/textBlockApi';
import { useTextViewStore } from '../../store/TextViewStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useImportStationTextStore } from '../../store/importStationTextStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

interface UseTextBlockResult {
  getTextBlockListForImport: (textId: string) => Promise<void>;
  selectTextForImport: (textId: string) => Promise<void>;
  createTextBlock: (data: CreateTextBlockReq) => Promise<void>;
  updateTextBlock: (data: UpdateTextBlockReq) => Promise<void>;
  deleteTextBlock: (data: DeleteTextBlockReq) => Promise<void>;
  reorderTextBlock: (data: UpdateReorderListReq) => Promise<void>;
}

export const useTextBlock = (): UseTextBlockResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setActiveTextBlockList = useTextViewStore((s) => s.setActiveTextBlockList);
  const setOnlyActiveTextBlockList = useTextViewStore((s) => s.setOnlyActiveTextBlockList);
  const afterDeleteActiveTextBlock = useTextViewStore((s) => s.afterDeleteActiveTextBlock);
  const setUpdatedTempAlert = useTempAlertStore((s) => s.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((s) => s.setDeletedTempAlert);
  const setSelectedId = useImportStationTextStore((s) => s.setSelectedId);
  const setSimpleSubList = useImportStationTextStore((s) => s.setSimpleSubList);
  const withApiHandler = useApiWrapper();

  const handleGetTextBlockListForImportSuccess = (response: TextBlockListResponse) => {
    const data = response.data;
    setSimpleSubList(data);
  };

  const handleSelectTextForImportSuccess = (
    response: TextBlockListResponse,
    textId: string,
  ) => {
    handleGetTextBlockListForImportSuccess(response);
    setSelectedId(textId);
  };

  const handleUpdateTextBlockSuccess = (response: TextBlockResponse) => {
    const data = response.data;
    const { activeTextBlockList } = useTextViewStore.getState();
    const newTextBlockList = activeTextBlockList.map((tb) => tb.id === data.id ? data : tb);
    setOnlyActiveTextBlockList(newTextBlockList);
    setUpdatedTempAlert();
  };

  const handleDeleteTextBlockSuccess = (_response: DeleteResponse, textBlockId: string) => {
    console.log("deleted well");
    afterDeleteActiveTextBlock(textBlockId);
    setDeletedTempAlert();
  };

  const handleCreateTextBlockSuccess = (response: TextBlockResponse) => {
    const data = response.data;
    const { activeTextBlockList } = useTextViewStore.getState();
    setOnlyActiveTextBlockList([...activeTextBlockList, { ...data, hasChanged: false }]);
  };

  const handleReorderTextBlockSuccess = (response: TextBlockListResponse) => {
    const data = response.data;
    setActiveTextBlockList(data);
  };

  const handleTextBlockRequest = async (
    action:
      | 'get list'
      | 'get list and select'
      | 'update'
      | 'create'
      | 'delete'
      | 'reorder',
    data: UpdateTextBlockReq | CreateTextBlockReq | DeleteTextBlockReq | UpdateReorderListReq | string
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'delete': {
          const deleteReq = data as DeleteTextBlockReq;
          await runCurrentArchiveRequest(
            aui,
            () => deleteTextBlock(aui, deleteReq),
            response => handleDeleteTextBlockSuccess(response, deleteReq.textBlockId),
          );
          break;
        }
        case 'update':
          await runCurrentArchiveRequest(
            aui,
            () => updateTextBlock(aui, data as UpdateTextBlockReq),
            handleUpdateTextBlockSuccess,
          );
          break;
        case 'create':
          await runCurrentArchiveRequest(
            aui,
            () => createTextBlock(aui, data as CreateTextBlockReq),
            handleCreateTextBlockSuccess,
          );
          break;
        case 'reorder':
          await runCurrentArchiveRequest(
            aui,
            () => reorderTextBlock(aui, data as UpdateReorderListReq),
            handleReorderTextBlockSuccess,
          );
          break;
        case 'get list':
          await runCurrentArchiveRequest(
            aui,
            () => getTextBlockListForOwner(aui, data as string),
            handleGetTextBlockListForImportSuccess,
          );
          break;
        case 'get list and select': {
          const textId = data as string;
          await runCurrentArchiveRequest(
            aui,
            () => getTextBlockListForOwner(aui, textId),
            response => handleSelectTextForImportSuccess(response, textId),
          );
          break;
        }
        default:
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getTextBlockListHandler = (textId: string) => handleTextBlockRequest('get list', textId);
  const selectTextForImportHandler = (textId: string) =>
    handleTextBlockRequest('get list and select', textId);
  const createTextBlockHandler = (data: CreateTextBlockReq) => handleTextBlockRequest('create', data);
  const updateTextBlockHandler = (data: UpdateTextBlockReq) => handleTextBlockRequest('update', data);
  const deleteTextBlockHandler = (data: DeleteTextBlockReq) => handleTextBlockRequest('delete', data);
  const reorderTextBlockHandler = (data: UpdateReorderListReq) => handleTextBlockRequest('reorder', data);

  return {
    getTextBlockListForImport: getTextBlockListHandler,
    selectTextForImport: selectTextForImportHandler,
    createTextBlock: createTextBlockHandler,
    updateTextBlock: updateTextBlockHandler,
    deleteTextBlock: deleteTextBlockHandler,
    reorderTextBlock: reorderTextBlockHandler,
  };
};
